import React from 'react';
import classNames from 'classnames';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ReCAPTCHA } from '@brokerbay/design-components';
import { Button, Icon } from 'components';
import { getInternalShowingDetailsUrl, featureEnabled } from 'utils';
import styles from './appointment_book.module.scss';
import PreviewButton from './PreviewButton';
import AppointmentSubHeader from './AppointmentSubHeader';

const AppointmentHeader = ({
  ModalService,
  appointment,
  auth,
  autoConfirm,
  errorReporter,
  extAuth,
  invalidTimeslot,
  isAdmin,
  isListingAgent,
  isListingContact,
  isMls,
  isTeamAdmin,
  isTimeChange,
  isMobile,
  listing,
  listingId,
  savedAppointment,
  showCaptcha,
  orgDetails,
  registeredOffers,
  partialAutoConfirm,
  isBookingVirtualTour,
  selectedTour,
  invitees,
  isSubmitting,
  customDuration,
  showingOverrideType,
  // functions
  setIsSubmitting,
  setHasMobileNumber,
  addPropertyToTour,
  updateState,
  clearSelected,
  submitAppointment,
  recordAppointment,
  verifyHuman,
  // angular
  $location,
  $rootScope,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const registeredOffersHandler = e => {
    const value = e.target.checked;
    updateState(draft => {
      draft.notifications.registeredOffers = value;
    });
  };

  const isInternalCoopDashboard =
    $location.path().startsWith('/my_business/appointment') ||
    $location.path().startsWith('/my_business/showing');

  return (
    <>
      {(!listing || !listing.id) && (
        <div className="normalheader transition animated fadeIn">
          <div className="hpanel">
            <div className="panel-body">
              {orgDetails && (
                <h2 className="font-light m-b-xs">
                  This property is currently not accepting online showings.
                  <br />
                  <br /> For more information, please call the listing
                  brokerage.
                  <br />
                  <br /> {orgDetails.name}
                  <br />
                  <br /> {orgDetails.phone}
                </h2>
              )}
            </div>
          </div>
        </div>
      )}

      {listing && listing.id && !savedAppointment && (
        <div
          className={`normalheader transition animated fadeIn ${styles.invisibleOnMobile}`}
        >
          <div className="hpanel">
            <div className="panel-body vcenter">
              <h2
                className="font-light col-md-6 vcenter"
                style={{ paddingLeft: 0, margin: 0 }}
              >
                {isInternalCoopDashboard && (
                  <a
                    className="btn btn-primary btn-sm"
                    style={{ marginRight: '5px' }}
                    href={getInternalShowingDetailsUrl(appointment.id)}
                  >
                    <i className="fa fa-chevron-left"></i> Back
                  </a>
                )}
                {((extAuth && extAuth.coopDashboard) ||
                  showingOverrideType) && (
                  <span className="vcenter">
                    <Button
                      type="link"
                      onClick={clearSelected}
                      icon={
                        <Icon
                          type="antdLeftOutlined"
                          offset={[0, showingOverrideType ? -9 : 0]}
                          color={'textPrimary'}
                        />
                      }
                      href={
                        !showingOverrideType
                          ? `/external/#/dash/${extAuth.dashboardId}/${
                              featureEnabled(
                                'showingDetailsRevampFeaturePhase',
                                2,
                              ) && extAuth?.coopDashboard
                                ? 'showing'
                                : 'appointments'
                            }/${appointment.id}`
                          : undefined
                      }
                    />
                  </span>
                )}
                <span className="hidden-xs">
                  {listing.line1} {listing.line2} &rsaquo;{' '}
                </span>
                <span style={{ paddingLeft: '5px' }}>
                  <AppointmentSubHeader
                    isTimeChange={isTimeChange}
                    isListingContact={isListingContact}
                    isMobile={isMobile}
                  />
                </span>
              </h2>

              <span className="col-md-6">
                {!isMls && !isInternalCoopDashboard && (
                  <a
                    className="btn btn-default pull-right margin-right"
                    href={`#/listing/${listing.id}/view`}
                  >
                    Cancel
                  </a>
                )}

                {appointment &&
                  !appointment.recordAppointment &&
                  isAdmin &&
                  !isBookingVirtualTour &&
                  !autoConfirm &&
                  !partialAutoConfirm &&
                  !showCaptcha && (
                    <Button
                      disabled={
                        !appointment.date ||
                        !appointment.time ||
                        isSubmitting ||
                        (customDuration.enabled &&
                          !customDuration.durationMinutes &&
                          !customDuration.durationHour)
                      }
                      onClick={() => {
                        submitAppointment(true);
                      }}
                      className="btn btn-success margin-right pull-right"
                      data-cy="submit-appointment-confirm"
                    >
                      Book & Confirm
                    </Button>
                  )}
                {!appointment.recordAppointment &&
                  !showCaptcha &&
                  !isBookingVirtualTour && (
                    <Button
                      onClick={async () => {
                        if (
                          isMls &&
                          !appointment.coop.phone &&
                          !extAuth?.hasFeature('SHOWING_AGENT_SMS')
                        ) {
                          setHasMobileNumber(false);
                          return;
                        }
                        setIsSubmitting(true);
                        if (isMls) {
                          if (executeRecaptcha) {
                            const token =
                              await executeRecaptcha('bookAppointment');
                            submitAppointment(false, null, token);
                          } else {
                            verifyHuman();
                          }
                        } else {
                          submitAppointment();
                        }
                      }}
                      className={classNames(
                        'btn btn-primary margin-right pull-right',
                        {
                          'btn-autoconfirm':
                            (featureEnabled('autoConfirmFeature')
                              ? listing.canRead ||
                                listing.canEdit ||
                                extAuth.coopDashboard ||
                                extAuth.retsDashboard
                              : isAdmin) &&
                            (autoConfirm || partialAutoConfirm),
                        },
                      )}
                      disabled={
                        !appointment.date ||
                        !appointment.time ||
                        !(
                          isAdmin ||
                          isListingAgent ||
                          isTeamAdmin ||
                          !invalidTimeslot
                        ) ||
                        isSubmitting ||
                        (customDuration.enabled &&
                          !customDuration.durationMinutes &&
                          !customDuration.durationHour)
                      }
                      data-cy="submit-appointment"
                    >
                      {(featureEnabled('autoConfirmFeature')
                        ? listing.canRead ||
                          listing.canEdit ||
                          extAuth.coopDashboard ||
                          extAuth.retsDashboard
                        : isAdmin || isTeamAdmin) &&
                      (autoConfirm || partialAutoConfirm)
                        ? 'AUTO-CONFIRM'
                        : 'Book Showing'}
                    </Button>
                  )}
                {extAuth && isBookingVirtualTour && !showCaptcha && (
                  <Button
                    disabled={!selectedTour}
                    className="btn btn-info margin-right pull-right"
                    onClick={async () => {
                      if (executeRecaptcha) {
                        const token = await executeRecaptcha('bookVirtualTour');
                        addPropertyToTour(
                          listing.id,
                          selectedTour.id,
                          invitees,
                          null,
                          token,
                        );
                      } else {
                        updateState(draft => {
                          draft.showCaptcha = true;
                        });
                      }
                    }}
                  >
                    Add Property to Tour
                  </Button>
                )}
                {!extAuth && isBookingVirtualTour && (
                  <Button
                    disabled={!selectedTour}
                    className="btn btn-info margin-right pull-right"
                    onClick={() =>
                      addPropertyToTour(listing.id, selectedTour.id, invitees)
                    }
                  >
                    Add Property to Tour
                  </Button>
                )}
                {appointment.recordAppointment && (
                  <Button
                    disabled={
                      !appointment.date || !appointment.time || isSubmitting
                    }
                    onClick={() => {
                      setIsSubmitting(true);
                      recordAppointment();
                    }}
                    className="btn btn-primary margin-right pull-right"
                    data-cy="record-appointment"
                  >
                    Record Showing
                  </Button>
                )}
                <PreviewButton
                  listingId={listingId}
                  auth={auth}
                  ModalService={ModalService}
                  errorReporter={errorReporter}
                  listing={listing}
                  $rootScope={$rootScope}
                  isBorderless
                />

                {isMls && (
                  <div
                    style={{ marginRight: '10px', clear: 'both' }}
                    className="pull-right m-t-sm"
                  >
                    {showCaptcha && (
                      <ReCAPTCHA
                        data-cy="recaptcha-challenge"
                        onChange={response => {
                          setIsSubmitting(true);
                          if (isBookingVirtualTour) {
                            addPropertyToTour(
                              listing.id,
                              selectedTour.id,
                              invitees,
                              response,
                            );
                          } else if (!isMobile) {
                            submitAppointment(false, response);
                          }
                        }}
                      />
                    )}
                  </div>
                )}
                {isMls &&
                  !(isListingContact && isTimeChange) &&
                  !showCaptcha && (
                    <div className="pull-right">
                      <label style={{ fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          checked={registeredOffers || false}
                          onChange={e => registeredOffersHandler(e)}
                        />{' '}
                        Notify me regarding registered offers
                      </label>
                    </div>
                  )}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppointmentHeader;
